import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Heart, Star, Gift, Sparkles, RotateCcw, Home } from 'lucide-react';
import { toast } from 'sonner';

function GratitudeBuilder({ onBack }) {
  const [currentExercise, setCurrentExercise] = useState(0);
  const [gratitudeEntries, setGratitudeEntries] = useState([]);
  const [currentEntry, setCurrentEntry] = useState('');
  const [points, setPoints] = useState(0);
  const [streak, setStreak] = useState(0);

  const gratitudeExercises = [
    {
      id: 1,
      title: "Daily Inventory",
      description: "What enriched your life today?",
      prompt: "Write about something you did that enriched your life or someone else's life",
      format: "When I [action], I felt [feeling] because I met my need for [need]",
      example: "When I took a break to call my friend, I felt warm and connected because I met my need for friendship."
    },
    {
      id: 2,
      title: "Someone Enriched You", 
      description: "How did someone make your life more wonderful?",
      prompt: "Think of something someone else did to enrich your life",
      format: "When [person] did [action], I felt [feeling] because it met my need for [need]",
      example: "When my partner made me tea without being asked, I felt touched because it met my need for care and consideration."
    },
    {
      id: 3,
      title: "Appreciation Expression",
      description: "Practice expressing gratitude to someone",
      prompt: "Write a gratitude you want to share with someone",
      format: "To [person]: When you [action], I feel [feeling] because my need for [need] is met",
      example: "To Sarah: When you listened to me vent yesterday, I felt so supported because my need for empathy was met."
    },
    {
      id: 4,
      title: "Celebration Practice",
      description: "Share in someone else's joy",
      prompt: "Think of someone's good news you want to celebrate",
      format: "Would you be willing to share in my celebration about [event/achievement]?",
      example: "Would you be willing to share in my celebration about getting promoted? I'm feeling so excited and grateful!"
    }
  ];

  const dailyPracticePrompts = [
    "Notice your hands. What do they do for you?",
    "Light a candle with grateful intention",
    "Send someone a message letting them know you're thinking of them",
    "Take 30 seconds to look at the sky",
    "Give someone a grateful hug (ask first!)",
    "Pick 5 things you don't need and give them away with joy",
    "Ask yourself: 'What has surpassed my expectations in life?'"
  ];

  const generateRandomPrompt = () => {
    const randomPrompt = dailyPracticePrompts[Math.floor(Math.random() * dailyPracticePrompts.length)];
    setCurrentEntry('');
    toast.success(`New practice: ${randomPrompt}`);
    return randomPrompt;
  };

  const addGratitudeEntry = () => {
    if (currentEntry.trim()) {
      setGratitudeEntries(prev => [...prev, {
        id: Date.now(),
        text: currentEntry,
        exercise: gratitudeExercises[currentExercise].title,
        timestamp: new Date().toLocaleString()
      }]);
      setPoints(prev => prev + 10);
      setStreak(prev => prev + 1);
      setCurrentEntry('');
      
      // Play reward sound
      const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAA5OTk5OTk5OTk5OTk5OTk5OTk5');
      audio.volume = 0.5;
      audio.play().catch(() => {});
      
      toast.success(`+10 points! Gratitude streak: ${streak + 1}`);
    }
  };

  return (
    <div 
      className="min-h-screen"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_belonging-sessions/artifacts/femlgvnm_ChatGPT%20Image%20Sep%2011%2C%202025%20at%2012_35_28%20AM.jpeg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="min-h-screen bg-green-900 bg-opacity-80 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-8">
            <Button onClick={onBack} variant="outline" className="border-white text-white hover:bg-white hover:text-black mb-6" style={{ borderRadius: 0 }}>
              ← Back to Games & Tools
            </Button>
            <h1 className="text-4xl font-bold text-white mb-4">💚 Gratitude Builder</h1>
            <p className="text-xl text-green-200">Fuel your compassionate consciousness with daily gratitude practice</p>
            
            {/* Points & Streak */}
            <div className="flex justify-center gap-6 mt-6">
              <Badge className="bg-yellow-500 text-black text-lg px-4 py-2">
                ⭐ {points} Points
              </Badge>
              <Badge className="bg-orange-500 text-white text-lg px-4 py-2">
                🔥 {streak} Streak
              </Badge>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            
            {/* Current Exercise */}
            <Card className="border-4 border-green-500 bg-white" style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-xl text-green-800 flex items-center gap-2">
                  <Heart className="w-5 h-5" />
                  {gratitudeExercises[currentExercise].title}
                </CardTitle>
                <p className="text-sm text-green-700">{gratitudeExercises[currentExercise].description}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-base text-gray-700">{gratitudeExercises[currentExercise].prompt}</p>
                  
                  <div className="bg-green-50 p-4 border-l-4 border-green-500">
                    <p className="text-sm font-bold text-green-800 mb-1">Format:</p>
                    <p className="text-sm text-green-700 italic">{gratitudeExercises[currentExercise].format}</p>
                    <p className="text-sm font-bold text-green-800 mt-2 mb-1">Example:</p>
                    <p className="text-sm text-green-700 italic">{gratitudeExercises[currentExercise].example}</p>
                  </div>
                  
                  <Textarea
                    placeholder="Write your gratitude here..."
                    value={currentEntry}
                    onChange={(e) => setCurrentEntry(e.target.value)}
                    className="border-green-500 min-h-[120px]"
                    style={{ borderRadius: 0 }}
                  />
                  
                  <div className="flex gap-3">
                    <Button 
                      onClick={addGratitudeEntry}
                      className="bg-green-600 text-white hover:bg-green-700 flex-1"
                      style={{ borderRadius: 0 }}
                      disabled={!currentEntry.trim()}
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Add to Gratitude Journal
                    </Button>
                    <Button 
                      onClick={() => setCurrentExercise((prev) => (prev + 1) % gratitudeExercises.length)}
                      variant="outline"
                      className="border-green-500 text-green-600"
                      style={{ borderRadius: 0 }}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Next Exercise
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Gratitude Journal */}
            <Card className="border-4 border-yellow-500 bg-white" style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-xl text-yellow-800 flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  Your Gratitude Journal
                </CardTitle>
                <p className="text-sm text-yellow-700">Collection of moments that made life wonderful</p>
              </CardHeader>
              <CardContent className="max-h-96 overflow-y-auto">
                {gratitudeEntries.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>Your gratitude collection will appear here</p>
                    <p className="text-sm">Start writing to build your compassion fuel!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {gratitudeEntries.slice(-10).reverse().map((entry) => (
                      <div key={entry.id} className="p-4 bg-yellow-50 border-l-4 border-yellow-500">
                        <div className="flex justify-between items-start mb-2">
                          <Badge className="bg-yellow-500 text-black text-xs">{entry.exercise}</Badge>
                          <span className="text-xs text-gray-500">{entry.timestamp}</span>
                        </div>
                        <p className="text-sm text-gray-700">{entry.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Daily Practice Prompt */}
          <Card className="mt-8 border-4 border-orange-500 bg-white" style={{ borderRadius: 0 }}>
            <CardHeader>
              <CardTitle className="text-xl text-orange-800">🌅 Today's Practice</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center space-y-4">
                <p className="text-lg text-gray-700" id="daily-prompt">
                  Click below for a random gratitude practice to try today
                </p>
                <Button 
                  onClick={() => {
                    const prompt = generateRandomPrompt();
                    document.getElementById('daily-prompt').textContent = prompt;
                  }}
                  className="bg-orange-600 text-white hover:bg-orange-700"
                  style={{ borderRadius: 0 }}
                >
                  🎲 Get Daily Practice
                </Button>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}

export default GratitudeBuilder;